<template>
  <fieldset :class="wrapperClasses">
    <b-field v-if="hasWork" class="file" :label="fileLabel">
      <b-upload v-if="uploadMode" v-model="file">
        <a class="button is-primary">
          <b-icon icon="upload"></b-icon>
          <span>Click to upload</span>
        </a>
      </b-upload>
      <b-input v-if="!uploadMode" v-model="filename" class="control"></b-input>
      <b-icon
        :icon="uploadModeIcon"
        @click.native="toggleUploadMode"
        :title="uploadModeToggleMessage"
        class="toggle-upload-mode"
      ></b-icon>
    </b-field>
    <b-field v-if="showWorkSelector" label="Add to work / create new">
      <b-select placeholder="Select.." v-model="workId">
        <option v-for="opt in otherWorks" :value="opt.key" :key="opt.key">{{opt.label}}</option>
      </b-select>
    </b-field>
    <b-field v-if="hasWork" label="Media title">
      <b-input v-model="title"></b-input>
    </b-field>
    <b-field label="Work title">
      <b-input v-model="workTitle" type="text"></b-input>
    </b-field>
    <b-field label="Work medium">
      <b-select placeholder="Select.." v-model="workMedium" required>
        <option v-for="opt in mediaOptions" :value="opt.key" :key="opt.key">{{opt.label}}</option>
      </b-select>
    </b-field>
    <b-field label="Year">
      <b-input v-model="workYear"></b-input>
    </b-field>
    <b-field label="Physical size">
      <b-input v-model="workSize"></b-input>
    </b-field>
    <b-field label="Work description">
      <b-input maxlength="500" type="textarea" v-model="workDescription"></b-input>
    </b-field>
    <b-button v-if="showSave" class="mini-save" type="is-success" @click="save">{{saveLabel}}</b-button>
  </fieldset>
</template>
<script>
import { mapGetters } from "vuex";
import { bus } from "../main";
import {
  saveFile,
  editMediaItem,
  saveResource,
  saveWork,
  getVimeoData
} from "../api/methods";
import { buildResourceRegex, validResourceUrl } from "../api/validators";
import { mediaItemSchema, workSchema } from "../store/schemas";
import {
  hasObjectId,
  notEmptyString,
  extractNestedOptions,
  fileHasChanged,
  emptyString
} from "../api/helpers";
import filters from "../mixins/filters";

export default {
  name: "WorkMediaEdt",
  components: {},
  props: {
    item: {
      type: Object,
      required: false,
      default() {
        return {
          _id: "",
          ...mediaItemSchema
        };
      }
    },
    work: {
      type: Object,
      required: false,
      default() {
        return {
          _id: "",
          ...workSchema
        };
      }
    }
  },
  mixins: [filters],
  data() {
    return {
      file: null,
      filename: "",
      title: "",
      workId: "new",
      workTitle: "",
      workMedium: "",
      workYear: null,
      workDescription: "",
      workSize: "",
      currYear: 2020,
      uploadMode: true,
      uploading: false,
      newImage: false
    };
  },
  mounted() {
    this.mapItem();
    this.currYear = new Date().getFullYear();
    bus.$on("escape", val => {
      if (val) {
        const { path } = this.$route;
        const parts = path.substring(1).split("/");
        if (parts[0] === "submissions" && parts.length > 1) {
          this.close();
        }
      }
    });

    bus.$on("media-item-removed", res => {
      if (res) {
        if (res === this._id) {
          this.newImage = true;
        }
      }
    });
  },
  computed: {
    ...mapGetters(["options"]),
    subId() {
      let subId = "";
      if (this.$parent.subId) {
        subId = this.$parent.subId;
      } else if (this.$parent.submission) {
        if (this.$parent.submission._id) {
          subId = this.$parent.submission._id;
        }
      }
      return subId;
    },
    isNew() {
      return !hasObjectId(this.item, 5) || this.newImage;
    },
    isNewWork() {
      return this.workId.length < 6;
    },
    mediaOptions() {
      let opts = extractNestedOptions("work", "medium", this.options);
      if (opts.length > 0) {
        opts = opts.map(o => {
          o.label = this.toWords(o.value);
          return o;
        });
      }
      return opts;
    },
    uploadModeIcon() {
      return this.uploadMode ? "vimeo" : "upload";
    },
    uploadModeToggleMessage() {
      return this.uploadMode ? "Add video URL" : "upload image or audio file";
    },
    hasWork() {
      return this.workId.length > 5;
    },
    showWorkSelector() {
      return this.isNew && !this.hasWork;
    },
    showSave() {
      return (
        (this.workId.length > 5 || this.workId === "new") &&
        this.workTitle.trim().length > 1 &&
        this.workMedium.length > 0
      );
    },
    saveLabel() {
      return this.isNewWork ? "Save new work" : "Save media & work details";
    },
    otherWorks() {
      let works = [];
      const newItem = {
        key: "new",
        label: "--- new work ---"
      };
      if (this.$parent.submission) {
        if (this.$parent.submission.works instanceof Array) {
          let invWs = [];
          if (this.$parent.submission.invalidWorks) {
            invWs = this.$parent.submission.invalidWorks;
          }
          works = this.$parent.submission.works
            .concat(invWs)
            .filter(w => {
              let valid = false;
              if (w instanceof Object) {
                if (w.title) {
                  valid = w.title.trim().length > 1;
                }
              }
              return valid;
            })
            .map(w => {
              return {
                key: w._id,
                label: w.title
              };
            });
        }
      }
      return [newItem, ...works];
    },
    wrapperClasses() {
      let cls = ["edit-work-media-item"];
      if (this.uploading) {
        cls.push("uploading");
      }
      return cls;
    },
    fileLabel() {
      return this.uploadMode ? "File" : "Video URL";
    }
  },
  watch: {
    file(cv, pv) {
      if (fileHasChanged(cv, pv)) {
        if (notEmptyString(this.subId, 5) && notEmptyString(this.workId, 5)) {
          this.$parent.updating = true;
          this.uploading = true;
          saveFile(this.subId, this.workId, this.item._id, cv).then(d => {
            if (d.mediaItem) {
              if (d.mediaItem instanceof Object && d.mediaItem.filename) {
                const wId = this.extractWorkId();
                const newWork = this.buildNewWork();
                setTimeout(() => {
                  bus.$emit("update-media-item", {
                    subId: this.subId,
                    workId: wId,
                    item: d.mediaItem,
                    newWork
                  });
                  this.toast(`File uploaded.`, 2000);
                  this.uploading = false;
                  this.$parent.updating = false;
                }, 10000);
                this.newImage = false;
                if (this.$parent.newMode) {
                  this.$parent.selectedMediaId = d.mediaItem._id;
                  this.toast(`Please wait while new file loads.`, 10000);
                  if (this.$parent.submission) {
                    if (this.$parent.submission.invalidWorks) {
                      this.$parent.submission.invalidWorks = this.$parent.submission.invalidWorks.filter(
                        w => w._id !== wId
                      );
                    }
                  }
                }
              }
            }
          });
        }
      }
    },
    workId(cv, pv) {
      if (pv !== cv) {
        if (this.$parent.submission) {
          if (this.$parent.submission.works) {
            if (cv.length > 5) {
              let w = this.$parent.submission.works.find(w => w._id === cv);
              if (w) {
                this.$parent.newWork = w;
              } else if (
                this.$parent.submission.invalidWorks instanceof Array
              ) {
                w = this.$parent.submission.invalidWorks.find(
                  w => w._id === cv
                );
                this.$parent.newWork = w;
              }
            } else {
              this.$parent.newWork = {
                _id: "",
                ...workSchema
              };
            }
            setTimeout(this.mapItem, 250);
          }
        }
      }
    }
  },
  methods: {
    mapItem() {
      if (this.$parent.showFull) {
        if (this.item instanceof Object) {
          if (notEmptyString(this.item.filename, 5)) {
            this.title = this.item.title;
            this.filename = this.item.filename;
            switch (this.item.type) {
              case "video":
                this.uploadMode = false;
                break;
              default:
                this.uploadMode = true;
                break;
            }
          }
        }
      }
      if (this.work instanceof Object) {
        if (hasObjectId(this.work, 5)) {
          this.workId = this.work._id;
        }

        if (notEmptyString(this.work.title, 1)) {
          this.workTitle = this.work.title.trim();
          this.workMedium = this.work.medium.trim();
          this.workYear = parseInt(this.work.year);
          this.workDescription = this.work.description.trim();
          this.workSize = this.work.size.trim();
        }
      }
      const { path } = this.$route;
      const workRef = path.split("/").pop();
      if (workRef.indexOf("-") > 3 && workRef.length > 16) {
        this.workId = workRef.split("-").pop();
      }
    },
    save() {
      const webFileChanged =
        !this.uploadMode &&
        this.filename.toString() !== this.item.filename.toString();
      if (
        !this.isNewWork &&
        (this.title !== this.item.title || webFileChanged)
      ) {
        let editedItem = {
          _id: this.item._id,
          title: this.title
        };
        if (webFileChanged) {
          if (validResourceUrl(this.filename)) {
            const match = this.filename.toString().match(buildResourceRegex());
            if (match) {
              getVimeoData(match[2]).then(data => {
                if (data.valid) {
                  editedItem = {
                    _id: this.item._id,
                    title: data.title,
                    sourceFilename: data.url,
                    filename: "vimeo://" + data.id,
                    type: "video",
                    mime: "video/vimeo",
                    attributes: {
                      duration: data.duration,
                      preview: data.thumbnail_large,
                      uploadDate: data.upload_date,
                      width: data.width,
                      height: data.height
                    }
                  };
                  this.saveMediaItem(editedItem, data);
                  this.newImage = true;
                }
              });
            }
          }
        } else {
          this.saveMediaItem(editedItem);
        }
      }
      const wId = this.extractWorkId();
      const editMode = notEmptyString(wId, 6);
      const editedWork = {
        _id: wId,
        title: this.workTitle,
        medium: this.workMedium,
        year: this.workYear,
        size: this.workSize,
        description: this.workDescription
      };
      saveWork(this.subId, editedWork).then(data => {
        if (data.work) {
          const { work } = data;
          bus.$emit("update-work", {
            subId: this.subId,
            workId: work._id,
            work
          });
          if (webFileChanged) {
            this.toast(`Please wait while new file loads.`);
          }
          if (this.$parent.newWork) {
            this.$parent.newWork = work;
          }
          const msg = this.isNew ? "New work saved" : "Work details saved";
          if (this.isNew && !editMode) {
            setTimeout(() => {
              const { query } = this.$route;
              this.workId = data.work._id;
              const newPath =
                "/submissions/" + this.subId + "/work-" + data.work._id;

              this.$router.push({
                path: newPath,
                query
              });
            }, 375);
          }
          this.toast(msg, 2000);
          if (!webFileChanged) {
            if (this.$parent.submission) {
              if (this.$parent.submission.invalidWorks instanceof Array) {
                this.$parent.submission.invalidWorks.push(work);
                this.$parent.submission.numAllWorks++;
              }
            }
          }
        }
      });
    },
    saveMediaItem(editedItem, fileInfo) {
      const hasId = hasObjectId(editedItem);
      const wId = this.extractWorkId();
      if (hasId) {
        editMediaItem(this.subId, wId, editedItem).then(this.handleMediaUpdate);
      } else {
        if (editedItem.filename) {
          if (fileInfo instanceof Object) {
            saveResource(this.subId, wId, fileInfo, "vimeo").then(
              this.handleMediaUpdate
            );
          }
        }
      }
    },
    extractWorkId() {
      let wId = this.work._id;
      if (emptyString(wId, 5) && notEmptyString(this.workId)) {
        wId = this.workId;
      }
      return wId;
    },
    handleMediaUpdate(data) {
      let item = null;
      if (data.item) {
        item = data.item;
      }
      if (data.submission) {
        if (data.submission.filename) {
          item = data.submission;
        }
      } else if (data.mediaItem) {
        if (data.mediaItem instanceof Object) {
          item = data.mediaItem;
        }
      }
      if (item instanceof Object) {
        const wId = this.extractWorkId();
        const newWork = this.buildNewWork();
        bus.$emit("update-media-item", {
          subId: this.subId,
          workId: wId,
          item,
          newWork
        });
      }
    },
    buildNewWork() {
      return {
        _id: this.extractWorkId(),
        title: this.workTitle,
        year: parseInt(this.workYear),
        medium: this.workMedium,
        size: this.workSize,
        description: this.workDescription
      };
    },
    toggleUploadMode() {
      this.uploadMode = !this.uploadMode;
    },
    close() {
      this.workId = "";
      this.workTitle = "";
    },
    toast(message = "", duration = 3000) {
      this.$buefy.toast.open({
        duration,
        message,
        position: "is-bottom",
        type: "is-success"
      });
    },
  }
};
</script>